import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input } from '@artosera/ui';
import type { ModuleAddon } from '@artosera/shared';
import { facturationVenioApi, type RemiseVue } from '../../lib/addons.js';
import { usePeut } from '../../auth/usePeut.js';

export function RemiseAddon({
  galerieId,
  addon,
  remise,
}: {
  galerieId: string;
  addon: ModuleAddon;
  remise: RemiseVue | undefined;
}) {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [points, setPoints] = useState('');
  const [fin, setFin] = useState('');
  const mutation = useMutation({
    mutationFn: (retirer: boolean) =>
      retirer
        ? facturationVenioApi.retirerRemise(galerieId, addon, {
            motif,
            revision: remise?.revision ?? 0,
          })
        : facturationVenioApi.remise(galerieId, addon, {
            pointsDeBase: Number(points),
            jusquAu: fin ? new Date(fin).toISOString() : null,
            motif,
            revision: remise?.revision ?? 0,
          }),
    onSuccess: async () => {
      setOuvert(false);
      await client.invalidateQueries({ queryKey: ['console', 'galerie', galerieId, 'addons'] });
    },
  });
  const active = remise && !remise.retiree;
  return (
    <div>
      {active ? (
        <p>
          {t('addons.remise.valeur', { points: remise.pointsDeBase })} ·{' '}
          {remise.jusquAu
            ? new Date(remise.jusquAu).toLocaleString(i18n.language)
            : t('addons.sansEcheance')}
        </p>
      ) : null}
      {peut('console:manage-addon-prices') ? (
        <Button
          onClick={() => {
            mutation.reset();
            setPoints(active ? String(remise.pointsDeBase) : '');
            const fin = active && remise.jusquAu ? new Date(remise.jusquAu) : null;
            setFin(
              fin
                ? `${fin.getFullYear()}-${String(fin.getMonth() + 1).padStart(2, '0')}-${String(fin.getDate()).padStart(2, '0')}T${String(fin.getHours()).padStart(2, '0')}:${String(fin.getMinutes()).padStart(2, '0')}`
                : '',
            );
            setMotif('');
            setOuvert(true);
          }}
        >
          {t('addons.remise.modifier')}
        </Button>
      ) : null}
      <Dialog
        open={ouvert}
        onOpenChange={(valeur) => {
          if (!mutation.isPending) setOuvert(valeur);
        }}
        title={t('addons.remise.titre')}
        closeLabel={t('addons.annuler')}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate(false);
          }}
        >
          <label htmlFor={`remise-${addon}`}>{t('addons.remise.points')}</label>
          <Input
            id={`remise-${addon}`}
            type="number"
            min={1}
            max={10000}
            required
            value={points}
            onChange={(e) => setPoints(e.target.value)}
          />
          <label htmlFor={`remise-fin-${addon}`}>{t('addons.jusquAu')}</label>
          <Input
            id={`remise-fin-${addon}`}
            type="datetime-local"
            value={fin}
            onChange={(e) => setFin(e.target.value)}
          />
          <label htmlFor={`remise-motif-${addon}`}>{t('addons.motif')}</label>
          <Input
            id={`remise-motif-${addon}`}
            required
            maxLength={500}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          {mutation.isError ? <p role="alert">{t('addons.echec')}</p> : null}
          <Button type="submit" disabled={mutation.isPending || !motif.trim()}>
            {t('addons.enregistrer')}
          </Button>
          {active ? (
            <Button
              type="button"
              disabled={mutation.isPending || !motif.trim()}
              onClick={() => mutation.mutate(true)}
            >
              {t('addons.remise.retirer')}
            </Button>
          ) : null}
        </form>
      </Dialog>
    </div>
  );
}
